import constants from '../qti/Constants';
import settingsReader from '../store/readHelpers/SettingsReader';
import excalidrawPreloadService from './ExcalidrawPreloadService';
import utilService from './UtilService';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import storeManager from '../store/StoreManager';
import questionHelperServices from './questionView/QuestionHelperServices';
import sessionConstants from '../constants/SessionConstants';
import isDrawingResponseIdentifierUtil from './DrawingUtils';
import postMessageService from './PostMessageService';
import apiService from './api/ApiService';
import UploadService from './questionView/UploadService';

class DrawingCanvasService {
    static getResponseService() {
        // Lazy import to break circular dependency
        if (!this.responseServiceCache) {
            const responseServiceModule = require('./ResponseService');
            this.responseServiceCache = responseServiceModule.default || responseServiceModule;
        }
        return this.responseServiceCache;
    }

    static isDrawingEnabled(question) {
        const enabled = (settingsReader.isCandidateDrawingOptionEnabled() &&
               question?.responseModels?.some(model => model.type === constants.interactionType.DRAWING_INTERACTION)) ||
               question?.objectType === constants.objectType.DRAWING;
        return !!enabled;
    }


    static getDrawingConfig(question) {
        if (!this.isDrawingEnabled(question)) {
            return { enabled: false };
        }

        const drawingInteraction = question.responseModels.find(model =>
            model.type === constants.interactionType.DRAWING_INTERACTION
        );

        const config = {
            enabled: true,
            allowOrientationOverride: drawingInteraction.allowOrientationOverride || false,
            canvasBackground: drawingInteraction.canvasBackground || null,
            maxHeight: drawingInteraction.maxHeight,
            maxWidth: drawingInteraction.maxWidth,
            prompt: drawingInteraction.prompt,
            responseIdentifier: drawingInteraction.responseIdentifier,
            interactionType: drawingInteraction.interactionType,
        };

        return config;
    }

    static isDrawingBaseIdentifier(identifier, question) {
        if (!identifier || !question) {
            return false;
        }
        return question.responseModels?.some(model =>
            model.type === constants.interactionType.DRAWING_INTERACTION && model.responseIdentifier === identifier
        );
    }

    static isDrawingBaseIdentifierByPostfix(identifier, responseIdentifiers) {
        if (!identifier || !Array.isArray(responseIdentifiers)) {
            return false;
        }
        const drawingExtraIdentifiers = this.getDrawingExtraIdentifiers(identifier);
        return drawingExtraIdentifiers.some(extraId =>
            responseIdentifiers.some(ri => ri.identifier === extraId)
        );
    }


    static createExtraResponses(responseIdentifier, fileInfo, options = {}) {
        const { jsonId = '', fileName = '', fileType = '', fileSize = '' } = fileInfo;
        const { offlineData = null, imageS3Uri = null, jsonS3Uri = null } = options;

        const extraResponses = [
            { identifier: `${responseIdentifier}_jsonId`, candidateResponse: [jsonId] },
            { identifier: `${responseIdentifier}_fileName`, candidateResponse: [fileName] },
            { identifier: `${responseIdentifier}_fileType`, candidateResponse: [fileType] },
            { identifier: `${responseIdentifier}_fileSize`, candidateResponse: [fileSize] },
        ];

        if (settingsReader.shouldStoreDrawingsAsUri()) {
            if (imageS3Uri) {
                extraResponses.push({
                    identifier: `${responseIdentifier}_fileURI`,
                    candidateResponse: [imageS3Uri],
                });
            }
            if (jsonS3Uri) {
                extraResponses.push({
                    identifier: `${responseIdentifier}_jsonDataURI`,
                    candidateResponse: [jsonS3Uri],
                });
            }
        }

        if (offlineData?.imageBase64 && offlineData?.sceneJson) {
            extraResponses.push(
                {
                    identifier: `${responseIdentifier}_drawingBase64`,
                    candidateResponse: [offlineData.imageBase64],
                },
                {
                    identifier: `${responseIdentifier}_jsonData`,
                    candidateResponse: [offlineData.sceneJson],
                },
                {
                    identifier: `${responseIdentifier}_pendingSync`,
                    candidateResponse: ['false'],
                }
            );
        }

        return extraResponses;
    }


    static getDrawingExtraIdentifiers(responseIdentifier) {
        return [
            `${responseIdentifier}_jsonId`,
            `${responseIdentifier}_fileName`,
            `${responseIdentifier}_fileType`,
            `${responseIdentifier}_fileSize`,
            `${responseIdentifier}_drawingBase64`,
            `${responseIdentifier}_jsonData`,
            `${responseIdentifier}_pendingSync`,
            `${responseIdentifier}_fileURI`,
            `${responseIdentifier}_jsonDataURI`,
        ];
    }


    static isDrawingResponseIdentifier(identifier) {
        return isDrawingResponseIdentifierUtil(identifier);
    }

    static getExtraResponses(userResponses, drawingConfig) {
        if (!userResponses || !drawingConfig?.responseIdentifier) {
            return [];
        }

        if (!drawingConfig.enabled) {
            return [];
        }

        const drawingExtraIdentifiers = this.getDrawingExtraIdentifiers(drawingConfig.responseIdentifier);

        const extraResponses = drawingExtraIdentifiers
            .filter(identifier => userResponses[identifier] && userResponses[identifier].length > 0)
            .map(identifier => ({
                identifier,
                userResponse: userResponses[identifier] || [],
            }));

        return extraResponses;
    }

    static hasExistingDrawing(userResponses, drawingConfig, extraResponses) {
        if (!drawingConfig?.enabled) {
            return false;
        }

        const drawingResponse = userResponses?.[drawingConfig?.responseIdentifier];
        const hasExistingDrawing = drawingResponse && drawingResponse.length > 0 && !!drawingResponse[0];
        const hasDrawingExtraResponses = (() => {
            if (!extraResponses || extraResponses.length === 0 || !drawingConfig?.responseIdentifier) return false;
            const responseIdentifier = drawingConfig.responseIdentifier;
            const fileNameObj = extraResponses.find(
                r => r.identifier === `${responseIdentifier}_fileName` || r.identifier === `${responseIdentifier}_drawingBase64`
            );
            const hasFileName =
            fileNameObj &&
            Array.isArray(fileNameObj.userResponse) &&
            fileNameObj.userResponse[0];

            return !!hasFileName;
        })();

        const result = hasExistingDrawing || hasDrawingExtraResponses;

        return result;
    }

    static sanitizeElement(element = {}) {
        if (!element || element.isDeleted) {
            return null;
        }
        const {
            version,
            versionNonce,
            updated,
            updatedBy,
            boundElements,
            link,
            locked,
            ...rest
        } = element;

        return rest;
    }

    static sanitizeAppState(appState = {}) {
        const volatileKeys = new Set([
            'scrollX', 'scrollY', 'zoom', 'offsetLeft', 'offsetTop',
            'editingGroupId', 'draggingElement', 'resizingElement', 'multiElement',
            'selectionElement', 'pendingImageElementId', 'editingLinearElement',
            'hoveredElement', 'selectedElementIds', 'selectedGroupIds',
            'toastMessage', 'elementType', 'exportBackground', 'pendingFileUploads',
        ]);

        return Object.keys(appState).reduce((sanitized, key) => {
            if (!volatileKeys.has(key)) {
                sanitized[key] = appState[key];
            }
            return sanitized;
        }, {});
    }

    static sanitizeFiles(files = {}) {
        return Object.keys(files || {})
            .sort((a, b) => a.localeCompare(b))
            .map((id) => {
                const file = files[id];
                if (!file) return null;
                const { created, lastRetrieved, ...sanitizedFile } = file;
                return { id, ...sanitizedFile };
            })
            .filter(Boolean);
    }

    static captureSnapshot(excalidrawRef) {
        if (!excalidrawRef?.current) {
            return null;
        }
        const elements = excalidrawRef.current.getSceneElements?.() || [];
        const appState = excalidrawRef.current.getAppState?.() || {};
        const files = excalidrawRef.current.getFiles?.() || {};

        // Check if canvas is empty (no elements or all deleted)
        const hasValidElements = elements.some(el => el && !el.isDeleted);
        if (!hasValidElements) {
            return null;
        }

        // Sanitize data to remove volatile fields for comparison
        const sanitizedElements = (elements || [])
            .map(this.sanitizeElement)
            .filter(Boolean)
            .sort((a, b) => (a?.id || '').localeCompare(b?.id || ''));
        const sanitizedAppState = this.sanitizeAppState(appState || {});
        const sanitizedFiles = this.sanitizeFiles(files || {});

        const serializeFn = excalidrawPreloadService.getSerializeAsJSON();
        if (typeof serializeFn === 'function') {
            return serializeFn(sanitizedElements, sanitizedAppState, sanitizedFiles);
        }

        return JSON.stringify({
            elements: sanitizedElements,
            appState: sanitizedAppState,
            files: sanitizedFiles,
        });
    }

    static createImageBlob(imageData, fileName) {
        const byteString = atob(imageData.split(',')[1]);
        const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        const imageBlob = new Blob([ab], { type: mimeString });
        imageBlob.name = fileName;
        return { blob: imageBlob, mimeType: mimeString };
    }

    static createJsonBlob(scene, jsonFileName) {
        const jsonString = JSON.stringify(scene);
        const jsonBlob = new Blob([jsonString], { type: 'application/json' });
        jsonBlob.name = jsonFileName;
        return jsonBlob;
    }


    static async loadDrawingDataFromUrl(jsonId, services) {
        if (!jsonId || !services?.uploadService?.getFileDownloadUrl) {
            return null;
        }

        const jsonUrl = services.uploadService.getFileDownloadUrl(jsonId);

        try {
            const response = await fetch(jsonUrl);
            if (!response.ok) {
                return null;
            }
            const data = await response.json();
            let elements = [];
            if (data.elements != null) {
                elements = data.elements;
            } else if (Array.isArray(data)) {
                elements = data;
            }
            return {
                elements,
                appState: data.appState || {},
                files: data.files || {},
            };
        } catch (err) {
            console.error('Failed to load drawing data:', err);
            return null;
        }
    }

    static getDrawingImageUrlFromId(userResponses, responseIdentifier, contentItemId, services) {
        if (!contentItemId) {
            return null;
        }
        const cachedImage = this.getCachedCanvasBackgroundImage(userResponses, responseIdentifier, contentItemId);
        if (cachedImage) {
            return cachedImage;
        }
        if (!services?.uploadService?.getFileDownloadUrl) {
            return null;
        }
        return services.uploadService.getFileDownloadUrl(contentItemId);
    }

    static isAuthorDrawingBgEditMode() {
        const mode = utilService.getUrlParam('authorDrawingBgEdit');
        return mode === 'true' || mode === '1';
    }

    static getExistingBackgroundJsonId() {
        const jsonId = utilService.getUrlParam('existingBackgroundJsonId');
        return jsonId ? parseInt(jsonId, 10) : null;
    }

    static normaliseBackgroundScene(data) {
        const sceneData = data || {};
        const fallbackElements = Array.isArray(sceneData) ? sceneData : [];
        const elements = sceneData.elements != null ? sceneData.elements : fallbackElements;

        return {
            elements,
            appState: sceneData.appState || {},
            files: sceneData.files || {},
        };
    }

    static async loadExistingBackgroundJson(userResponses, responseIdentifier, jsonContentItemId, services) {
        if (!jsonContentItemId) {
            return null;
        }

        const jsonIdentifier = `${responseIdentifier}_canvasbg_${jsonContentItemId}_jsonData`;
        if (userResponses?.[jsonIdentifier]?.[0]) {
            return this.normaliseBackgroundScene(JSON.parse(userResponses[jsonIdentifier][0]));
        }

        if (this.isOffline()) {
            return null;
        }

        if (!services?.uploadService?.getFileDownloadUrl) {
            return null;
        }
        const jsonUrl = services.uploadService.getFileDownloadUrl(jsonContentItemId);

        try {
            const response = await fetch(jsonUrl);
            if (!response.ok) {
                return null;
            }
            const data = await response.json();
            return this.normaliseBackgroundScene(data);
        } catch (err) {
            console.error('Failed to load existing drawing background JSON:', err);
            return null;
        }
    }

    static async loadAuthorBackgroundForStudent(question, drawingConfig, userResponses, jsonContentItemId, services) {
        const jsonIdentifier = `${drawingConfig.responseIdentifier}_canvasbg_${jsonContentItemId}_jsonData`;
        if (userResponses?.[jsonIdentifier]?.[0]) {
            return this.normaliseBackgroundScene(JSON.parse(userResponses[jsonIdentifier][0]));
        }

        const data = await this.loadExistingBackgroundJson(userResponses, drawingConfig.responseIdentifier, jsonContentItemId, services);
        if (data) {
            const handleUserResponse = this.getResponseService().handleUserResponse.bind(this.getResponseService());
            const contentItemId = drawingConfig.canvasBackground?.contentItemId;
            if (contentItemId) {
                if (!services?.uploadService?.getFileDownloadUrl) {
                    return data;
                }
                const imageUrl = services.uploadService.getFileDownloadUrl(contentItemId);
                try {
                    const imageResponse = await fetch(imageUrl);
                    if (imageResponse.ok) {
                        const imageBase64 = await this.fetchAndConvertImageToBase64(imageResponse);
                        this.storeCanvasBackgroundInRedux(
                            question,
                            drawingConfig,
                            handleUserResponse,
                            contentItemId,
                            jsonContentItemId,
                            imageBase64,
                            JSON.stringify(data)
                        );
                    }
                } catch (err) {
                    console.error('Failed to fetch image for canvas background:', err);
                }
            }
            return data;
        }
        return null;
    }

    static async preloadCanvasBackground(question, drawingConfig, handleUserResponse, userResponses, contentItemId, jsonContentItemId, services) {
        if (!contentItemId || !jsonContentItemId || !services?.uploadService) {
            return null;
        }

        const imageIdentifier = `${drawingConfig.responseIdentifier}_canvasbg_${contentItemId}_drawingBase64`;
        const jsonIdentifier = `${drawingConfig.responseIdentifier}_canvasbg_${jsonContentItemId}_jsonData`;

        if (userResponses?.[imageIdentifier]?.[0] && userResponses?.[jsonIdentifier]?.[0]) {
            return {
                imageBase64: userResponses[imageIdentifier][0],
                jsonData: JSON.parse(userResponses[jsonIdentifier][0]),
            };
        }

        if (this.isOffline()) {
            return null;
        }

        try {
            const imageUrl = services.uploadService.getFileDownloadUrl(contentItemId);
            const jsonUrl = services.uploadService.getFileDownloadUrl(jsonContentItemId);

            const [imageResponse, jsonResponse] = await Promise.all([
                fetch(imageUrl),
                fetch(jsonUrl),
            ]);

            if (!imageResponse.ok || !jsonResponse.ok) {
                return null;
            }

            const imageBase64 = await this.fetchAndConvertImageToBase64(imageResponse);
            const jsonData = await jsonResponse.json();
            const normalizedJson = jsonData.elements ? jsonData : { elements: jsonData };

            this.storeCanvasBackgroundInRedux(
                question,
                drawingConfig,
                handleUserResponse,
                contentItemId,
                jsonContentItemId,
                imageBase64,
                JSON.stringify(normalizedJson)
            );

            return {
                imageBase64,
                jsonData: normalizedJson,
            };
        } catch (err) {
            console.error('Failed to preload canvas background:', err);
            return null;
        }
    }

    static getCachedCanvasBackgroundImage(userResponses, responseIdentifier, contentItemId) {
        if (!contentItemId) {
            return null;
        }

        const imageIdentifier = `${responseIdentifier}_canvasbg_${contentItemId}_drawingBase64`;
        return userResponses?.[imageIdentifier]?.[0] || null;
    }

    static async exportDrawingForAuthorBackground(excalidrawRef, question) {
        if (!excalidrawRef?.current) {
            throw new Error('Excalidraw ref is not available');
        }

        const itemBankId = parseInt(utilService.getUrlParam('itemBankId'), 10);
        if (!itemBankId) {
            throw new Error('itemBankId not found in URL parameters');
        }

        const exportResult = await this.exportDrawingToImage(excalidrawRef);
        if (!exportResult) {
            throw new Error('Failed to export drawing');
        }

        const { finalImageData, elements, appState, files } = exportResult;
        const contentRevisionId = question.contentRevisionId;
        const fileName = `${contentRevisionId}-canvas_background.png`;
        const jsonFileName = `${contentRevisionId}-canvas_background.json`;

        const { blob: imageBlob } = this.createImageBlob(finalImageData, fileName);
        const scene = { elements, appState, files };
        const jsonBlob = this.createJsonBlob(scene, jsonFileName);

        const uploader = await UploadService.getInstance();

        const uploadFile = async (blob, fileFileName) => {
            const ticketResponse = await apiService.getAssetUploadTicket(itemBankId, fileFileName, blob.size);
            if (!ticketResponse.success || !ticketResponse.ticket) {
                throw new Error('Failed to get upload ticket');
            }

            return new Promise((resolve, reject) => {
                uploader.uploadFile(
                    ticketResponse.key,
                    blob,
                    ticketResponse.ticket,
                    (type, file, payload) => {
                        if (type === 'complete') {
                            apiService.assetUploadSuccess(itemBankId, ticketResponse.ticket, fileFileName)
                                .then((response) => {
                                    if (!response.success || !response.contentRevision?.contentItemId) {
                                        reject(new Error('Failed to get contentItemId from upload response'));
                                    } else {
                                        resolve(response.contentRevision.contentItemId);
                                    }
                                })
                                .catch(reject);
                        } else if (type === 'error') {
                            reject(new Error(payload || 'Upload failed'));
                        }
                    }
                );
            });
        };

        try {
            const [imageContentItemId, jsonContentItemId] = await Promise.all([
                uploadFile(imageBlob, fileName),
                uploadFile(jsonBlob, jsonFileName),
            ]);

            if (window.parent && window.parent !== window) {
                postMessageService.postTo(window.parent, {
                    type: 'canvas-background-uploaded',
                    imageContentItemId,
                    jsonContentItemId,
                    contentRevisionId,
                    fileName,
                });
            } else {
                throw new Error('Parent window not available');
            }
        } catch (error) {
            console.error('Failed to upload canvas background:', error);
            throw error;
        }
    }

    static async exportDrawingToImage(excalidrawRef) {
        if (!excalidrawRef?.current?.getSceneElements || !excalidrawRef?.current?.getAppState) {
            return null;
        }

        const elements = excalidrawRef.current.getSceneElements();
        const appState = excalidrawRef.current.getAppState();
        const files = excalidrawRef.current.getFiles ? excalidrawRef.current.getFiles() : undefined;

        await excalidrawPreloadService.getExcalidrawComponent();
        const exportToCanvas = excalidrawPreloadService.getExportToCanvas();

        const imageData = await exportToCanvas({
            elements,
            appState,
            files,
        });

        if (!imageData) {
            return null;
        }

        return {
            imageData,
            elements,
            appState,
            files,
            finalImageData: imageData.toDataURL('image/png'),
        };
    }

    static createOfflineExtraResponses(responseIdentifier, imageData, sceneJson) {
        return [
            {
                identifier: `${responseIdentifier}_drawingBase64`,
                candidateResponse: [imageData],
            },
            {
                identifier: `${responseIdentifier}_jsonData`,
                candidateResponse: [sceneJson],
            },
            {
                identifier: `${responseIdentifier}_pendingSync`,
                candidateResponse: ['true'],
            },
        ];
    }

    static async saveDrawingToServer(options) {
        const {
            excalidrawRef,
            services,
            question,
            drawingConfig,
            isDarkModeEnabled,
            handleUserResponse,
            isAuthorDrawingBgEdit = false,
        } = options;

        if (!excalidrawRef?.current || !services || !question) {
            throw new Error('Missing required parameters for saving drawing');
        }

        const exportResult = await this.exportDrawingToImage(excalidrawRef);
        if (!exportResult) {
            throw new Error('Failed to export drawing');
        }

        const { finalImageData, elements, appState, files } = exportResult;

        if (this.isOffline()) {
            return this.saveDrawingOffline(
                finalImageData,
                elements,
                appState,
                files,
                question,
                drawingConfig,
                handleUserResponse
            );
        }

        const state = storeManager.getState();
        const context = state?.session?.context;
        const isPreviewContext = context === sessionConstants.CONTEXT_PREVIEW || context === sessionConstants.CONTEXT_PREVIEW_DRAFT;
        const isDemoExam = state?.session?.sessionInfo?.demoExam || false;
        // In authorDrawingBgEdit mode, always upload to server even in preview context
        const shouldSkipS3Upload = ((isPreviewContext || isDemoExam) && !isAuthorDrawingBgEdit) || !services?.uploadService;

        if (shouldSkipS3Upload) {
            return this.saveDrawingLocally(
                excalidrawRef,
                question,
                drawingConfig,
                isDarkModeEnabled,
                handleUserResponse
            );
        }

        const contentRevisionId = question.contentRevisionId;
        const responseIdentifier = drawingConfig?.responseIdentifier;
        // In authorDrawingBgEdit mode, use canvas_background as the identifier
        const fileIdentifier = isAuthorDrawingBgEdit ? 'canvas_background' : responseIdentifier;
        const fileName = `${contentRevisionId}-${fileIdentifier}.png`;
        const jsonFileName = `${contentRevisionId}-${fileIdentifier}.json`;

        const { blob: imageBlob, mimeType } = this.createImageBlob(finalImageData, fileName);
        const scene = { elements, appState, files };
        const jsonBlob = this.createJsonBlob(scene, jsonFileName);

        // In authorDrawingBgEdit mode, always skip content URL creation to avoid assessment run dependency
        const skipContentUrlCreation = isAuthorDrawingBgEdit || settingsReader.shouldStoreDrawingsAsUri();

        return new Promise((resolve, reject) => {
            const uploadState = {
                imageContentItemId: null,
                jsonContentItemId: null,
                imageS3Uri: null,
                jsonS3Uri: null,
                imageUploaded: false,
                jsonUploaded: false,
            };

            const onBothUploaded = () => {
                this.handleBothUploadsComplete({
                    uploadState,
                    skipContentUrlCreation,
                    services,
                    question,
                    sceneData: { scene, elements },
                    drawingConfig,
                    fileInfo: { fileName, fileType: mimeType, imageBlob, finalImageData },
                    contentRevisionId,
                    handleUserResponse,
                    resolve,
                });
            };

            const uploadCallback = this.createUploadCallback({
                skipContentUrlCreation,
                fileNames: {
                    imageFileName: fileName,
                    jsonFileName,
                    imageBlobName: imageBlob.name,
                    jsonBlobName: jsonBlob.name,
                },
                state: uploadState,
                onBothUploaded,
                onError: reject,
            });

            services.uploadService.upload(imageBlob, question.contentItemId, question.contentRevisionId, uploadCallback, skipContentUrlCreation);
            services.uploadService.upload(jsonBlob, question.contentItemId, question.contentRevisionId, uploadCallback, skipContentUrlCreation);
        });
    }

    static async checkAndAutoSave({
        excalidrawRef,
        showExcalidraw,
        services,
        question,
        drawingConfig,
        isDarkModeEnabled,
        handleUserResponse,
        isEdited,
        lastSaveTime,
        autoSaveFrequency,
        isSaving,
        isSavingLocally,
    }) {
        if (!showExcalidraw || !excalidrawRef?.current || !services || !question) {
            return { shouldSave: false, reason: 'missing_requirements' };
        }

        if (!excalidrawRef.current.getSceneElements || !excalidrawRef.current.getAppState) {
            return { shouldSave: false, reason: 'excalidraw_not_ready' };
        }

        if (this.isOffline()) {
            return { shouldSave: false, reason: 'offline' };
        }

        const now = Date.now();
        const timeSinceLastSave = lastSaveTime ? now - lastSaveTime : Infinity;
        const requiredTime = autoSaveFrequency * 1000;

        if (timeSinceLastSave < requiredTime) {
            return { shouldSave: false, reason: 'too_soon' };
        }

        if (!isEdited || isSaving || isSavingLocally) {
            return { shouldSave: false, reason: 'not_edited_or_saving' };
        }

        try {
            const result = await this.saveDrawingToServer({
                excalidrawRef,
                services,
                question,
                drawingConfig,
                isDarkModeEnabled,
                handleUserResponse,
                isAuthorDrawingBgEdit: false,
            });

            if (result) {
                return {
                    shouldSave: true,
                    result,
                    snapshot: this.captureSnapshot(excalidrawRef),
                };
            }

            return { shouldSave: false, reason: 'no_result' };
        } catch (err) {
            return { shouldSave: false, reason: 'error', error: err };
        }
    }

    static shouldApplyDarkMode(contrastOption) {
        return contrastOption === 'yellow-on-black' || contrastOption === 'inverted';
    }

    static getExcalidrawTheme(isDarkModeEnabled) {
        return isDarkModeEnabled ? 'dark' : 'light';
    }

    static isOffline() {
        return !navigator.onLine || !utilService.isBrowserStatusOnline();
    }


    static extractContentItemId(payload) {
        if (!payload) {
            return null;
        }
        return payload.contentItemId || payload.contentRevisionId || payload;
    }

    static handleBothUploadsComplete(config) {
        const {
            uploadState,
            skipContentUrlCreation,
            services,
            question,
            sceneData = {},
            drawingConfig,
            fileInfo = {},
            contentRevisionId,
            handleUserResponse,
            resolve,
        } = config;

        const { scene = null, sceneJson = null, elements = null } = sceneData;
        const { fileName = '', fileType = '', imageBlob = null, finalImageData = null } = fileInfo;

        const hasS3Uris = uploadState.imageS3Uri && uploadState.jsonS3Uri;
        const hasContentItemIds = uploadState.imageContentItemId && uploadState.jsonContentItemId;
        const isReady = skipContentUrlCreation ? hasS3Uris : hasContentItemIds;

        if (isReady) {
            const imageUrl = skipContentUrlCreation
                ? uploadState.imageS3Uri
                : services.uploadService.getFileDownloadUrl(uploadState.imageContentItemId);

            const jsonData = sceneJson || JSON.stringify(scene);
            const drawingExtraResponses = this.createExtraResponses(
                drawingConfig?.responseIdentifier || drawingConfig.responseIdentifier,
                {
                    imageId: uploadState.imageContentItemId || '',
                    jsonId: uploadState.jsonContentItemId || '',
                    fileName,
                    fileType,
                    fileSize: String(imageBlob?.size || 0),
                },
                {
                    offlineData: finalImageData ? { imageBase64: finalImageData, sceneJson: jsonData } : { imageBase64: null, sceneJson: jsonData },
                    imageS3Uri: uploadState.imageS3Uri,
                    jsonS3Uri: uploadState.jsonS3Uri,
                }
            );

            const interactionIdentifier = drawingConfig?.responseIdentifier || drawingConfig.responseIdentifier;
            const isDrawingQuestionType = question?.responseModels?.length === 1 &&
                question.responseModels[0].type === constants.interactionType.DRAWING_INTERACTION;
            // For drawing question type
            const mainResponseId = uploadState.imageContentItemId || (isDrawingQuestionType ? 'user-drawing' : '');
            handleUserResponse(
                contentRevisionId,
                interactionIdentifier,
                [String(mainResponseId)],
                drawingExtraResponses
            );

            const result = {
                imageUrl,
                imageContentItemId: uploadState.imageContentItemId,
                jsonContentItemId: uploadState.jsonContentItemId,
            };
            if (elements) {
                result.elements = elements;
            }
            resolve(result);
        }
    }

    static parseExistingDrawingData(existingBase64, existingJsonData) {
        if (!existingBase64 || !existingJsonData) {
            return null;
        }
        try {
            const scene = JSON.parse(existingJsonData);
            return {
                imageUrl: existingBase64,
                elements: scene.elements,
                appState: scene.appState,
                files: scene.files,
                isOffline: false,
            };
        } catch (err) {
            console.error('Failed to parse existing drawing data:', err);
            return null;
        }
    }

    static async fetchAndConvertImageToBase64(imageResponse) {
        const imageBlob = await imageResponse.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(imageBlob);
        });
    }

    static async fetchAndProcessDrawingData(imageResponse, jsonResponse, existingBase64, existingJsonData) {
        if (!imageResponse.ok || !jsonResponse.ok) {
            const fallbackData = this.parseExistingDrawingData(existingBase64, existingJsonData);
            return fallbackData || null;
        }

        const imageBase64 = await this.fetchAndConvertImageToBase64(imageResponse);
        const sceneJson = await jsonResponse.text();
        const scene = JSON.parse(sceneJson);

        return {
            imageBase64,
            sceneJson,
            scene,
        };
    }

    static createDrawingExtraResponses(responseIdentifier, imageBase64, sceneJson) {
        return [
            {
                identifier: `${responseIdentifier}_drawingBase64`,
                candidateResponse: [imageBase64],
            },
            {
                identifier: `${responseIdentifier}_jsonData`,
                candidateResponse: [sceneJson],
            },
            {
                identifier: `${responseIdentifier}_pendingSync`,
                candidateResponse: ['false'],
            },
        ];
    }

    static storeDrawingDataInXML(question, drawingConfig, handleUserResponse, userResponses, imageBase64, sceneJson, mainResponseId = '') {
        const extraResponses = this.createDrawingExtraResponses(
            drawingConfig.responseIdentifier,
            imageBase64,
            sceneJson
        );
        let mainResponseValue = mainResponseId || userResponses?.[drawingConfig.responseIdentifier]?.[0] || '';
        const isDrawingQuestionType = question?.responseModels?.length === 1 &&
                                      question.responseModels[0].type === constants.interactionType.DRAWING_INTERACTION;
        if (isDrawingQuestionType && imageBase64 && sceneJson && !mainResponseValue) {
            mainResponseValue = 'user-drawing';
        }
        handleUserResponse(
            question.contentRevisionId,
            drawingConfig.responseIdentifier,
            [mainResponseValue],
            extraResponses
        );
    }

    static storeCanvasBackgroundInRedux(question, drawingConfig, handleUserResponse, contentItemId, jsonContentItemId, imageBase64, jsonData) {
        const extraResponses = [
            {
                identifier: `${drawingConfig.responseIdentifier}_canvasbg_${contentItemId}_drawingBase64`,
                candidateResponse: [imageBase64],
            },
            {
                identifier: `${drawingConfig.responseIdentifier}_canvasbg_${jsonContentItemId}_jsonData`,
                candidateResponse: [jsonData],
            },
        ];
        handleUserResponse(
            question.contentRevisionId,
            drawingConfig.responseIdentifier,
            [''],
            extraResponses
        );
    }

    static createUploadCallback(config) {
        const { skipContentUrlCreation, fileNames, state, onBothUploaded, onError } = config;

        return (type, file, payload) => {
            if (type === 'sent-upload-success') {
                this.handleUploadSuccess(type, file, payload, skipContentUrlCreation, fileNames, state);
                const bothReady = skipContentUrlCreation
                    ? (state.imageUploaded && state.jsonUploaded && state.imageS3Uri && state.jsonS3Uri)
                    : (state.imageUploaded && state.jsonUploaded);

                if (bothReady) {
                    onBothUploaded();
                }
            } else if (type === 'sent-upload-error') {
                onError(new Error('Upload failed'));
            }
        };
    }

    static handleUploadSuccess(type, file, payload, skipContentUrlCreation, fileNames, state) {
        const { imageFileName, jsonFileName, imageBlobName, jsonBlobName } = fileNames;

        if (skipContentUrlCreation) {
            const s3Url = payload?.s3Url;
            if (s3Url) {
                const isImage = file.name && (file.name.endsWith('.png') || file.name === imageFileName || file.name === imageBlobName);
                const isJson = file.name && (file.name.endsWith('.json') || file.name === jsonFileName || file.name === jsonBlobName);
                if (isImage && !state.imageUploaded) {
                    state.imageS3Uri = s3Url;
                    state.imageUploaded = true;
                } else if (isJson && !state.jsonUploaded) {
                    state.jsonS3Uri = s3Url;
                    state.jsonUploaded = true;
                }
            }
        } else {
            const contentItemId = this.extractContentItemId(payload);
            if (file.name === imageFileName || file.name === imageBlobName) {
                state.imageContentItemId = contentItemId;
                state.imageUploaded = true;
            } else {
                state.jsonContentItemId = contentItemId;
                state.jsonUploaded = true;
            }
        }
    }


    static extractS3UrisFromResponses(questionResponse, responseIdentifier) {
        if (!questionResponse?.responseIdentifiers) {
            return { imageS3Uri: null, jsonS3Uri: null };
        }

        const fileUriResponse = questionResponse.responseIdentifiers.find(
            r => r.identifier === `${responseIdentifier}_fileURI`
        );
        const jsonDataUriResponse = questionResponse.responseIdentifiers.find(
            r => r.identifier === `${responseIdentifier}_jsonDataURI`
        );

        const imageS3Uri = fileUriResponse?.userResponse?.[0] || null;
        const jsonS3Uri = jsonDataUriResponse?.userResponse?.[0] || null;

        return {
            imageS3Uri,
            jsonS3Uri,
        };
    }


    static resolvePresignedUrls(questionResponse, responseIdentifier) {
        if (!questionResponse?.presignedUrls) {
            return { imagePresignedUrl: null, jsonPresignedUrl: null };
        }

        const { imageS3Uri, jsonS3Uri } = this.extractS3UrisFromResponses(
            questionResponse,
            responseIdentifier
        );

        const presignedUrls = questionResponse.presignedUrls;
        const imagePresignedUrl = imageS3Uri ? presignedUrls[imageS3Uri] || null : null;
        const jsonPresignedUrl = jsonS3Uri ? presignedUrls[jsonS3Uri] || null : null;

        return {
            imagePresignedUrl,
            jsonPresignedUrl,
        };
    }


    static async loadDrawingDataFromPresignedUrl(presignedUrl) {
        if (!presignedUrl) {
            return null;
        }

        try {
            const response = await fetch(presignedUrl);
            if (!response.ok) {
                return null;
            }
            const data = await response.json();
            let elements = [];
            if (data.elements != null) {
                elements = data.elements;
            } else if (Array.isArray(data)) {
                elements = data;
            }
            return {
                elements,
                appState: data.appState || {},
                files: data.files || {},
            };
        } catch (err) {
            console.error('Failed to load drawing data from presigned URL:', err);
            return null;
        }
    }


    static async tryPreloadDrawingWithPresignedUrls(question, drawingConfig, userResponses, handleUserResponse, forceFetch = false) {
        if (!question || !drawingConfig) {
            return null;
        }

        if (!settingsReader.shouldStoreDrawingsAsUri()) {
            return null;
        }

        const state = storeManager.getState();
        const questionResponse = state.response[question.contentRevisionId];
        if (!questionResponse) {
            return null;
        }

        const { imagePresignedUrl, jsonPresignedUrl } = this.resolvePresignedUrls(
            questionResponse,
            drawingConfig.responseIdentifier
        );

        if (!imagePresignedUrl || !jsonPresignedUrl) {
            return null;
        }

        return this.preloadDrawingWithPresignedUrls(
            imagePresignedUrl,
            jsonPresignedUrl,
            question,
            drawingConfig,
            handleUserResponse,
            userResponses,
            forceFetch
        );
    }

    static resolveDrawingUrls(question, drawingConfig, userResponses, services) {
        const imageId = userResponses?.[drawingConfig?.responseIdentifier]?.[0];
        const jsonId = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonId`]?.[0];

        let imageUrl = null;
        let jsonUrl = null;
        let usePresignedUrls = false;

        if (settingsReader.shouldStoreDrawingsAsUri() && question) {
            const state = storeManager.getState();
            const questionResponse = state.response[question.contentRevisionId];
            if (questionResponse) {
                const { imagePresignedUrl, jsonPresignedUrl } = this.resolvePresignedUrls(
                    questionResponse,
                    drawingConfig?.responseIdentifier
                );

                if (imagePresignedUrl) {
                    imageUrl = imagePresignedUrl;
                    usePresignedUrls = true;
                }

                if (jsonPresignedUrl) {
                    jsonUrl = jsonPresignedUrl;
                    usePresignedUrls = true;
                }
            }
        }


        if (!imageUrl && imageId && typeof imageId === 'string' && imageId.trim() !== '' && services?.uploadService) {
            imageUrl = services.uploadService.getFileDownloadUrl(imageId);
        }

        if (!jsonUrl && jsonId && typeof jsonId === 'string' && jsonId.trim() !== '' && services?.uploadService) {
            jsonUrl = services.uploadService.getFileDownloadUrl(jsonId);
        }

        return { imageUrl, jsonUrl, usePresignedUrls };
    }

    static async saveDrawingLocally(
        excalidrawRef,
        question,
        drawingConfig,
        isDarkModeEnabled,
        handleUserResponse
    ) {
        if (!excalidrawRef.current || !question) {
            return null;
        }

        const exportResult = await this.exportDrawingToImage(excalidrawRef);
        if (!exportResult) {
            return null;
        }

        const { finalImageData, elements, appState, files } = exportResult;
        const scene = { elements, appState, files };
        const sceneJson = JSON.stringify(scene);

        const extraResponses = this.createOfflineExtraResponses(
            drawingConfig.responseIdentifier,
            finalImageData,
            sceneJson
        );

        const isDrawingQuestionType = question?.responseModels?.length === 1 &&
            question.responseModels[0].type === constants.interactionType.DRAWING_INTERACTION;
        const mainResponseValue = isDrawingQuestionType ? ['user-drawing'] : [''];

        handleUserResponse(
            question.contentRevisionId,
            drawingConfig.responseIdentifier,
            mainResponseValue,
            extraResponses
        );

        return {
            imageUrl: finalImageData,
            elements,
            isLocalOnly: true,
        };
    }

    static async saveDrawingOffline(imageData, elements, appState, files, question, drawingConfig, handleUserResponse) {
        const scene = { elements, appState, files };
        const sceneJson = JSON.stringify(scene);

        const extraResponses = this.createOfflineExtraResponses(
            drawingConfig.responseIdentifier,
            imageData,
            sceneJson
        );

        const isDrawingQuestionType = question?.responseModels?.length === 1 &&
                                      question.responseModels[0].type === constants.interactionType.DRAWING_INTERACTION;
        const mainResponseValue = isDrawingQuestionType && imageData && sceneJson ? 'user-drawing' : '';
        handleUserResponse(
            question.contentRevisionId,
            drawingConfig.responseIdentifier,
            [mainResponseValue],
            extraResponses
        );

        return {
            imageUrl: imageData,
            elements,
            isOffline: true,
        };
    }

    static loadOfflineDrawingData(userResponses, drawingConfig, services = null) {
        if (!drawingConfig?.responseIdentifier) {
            return null;
        }

        const base64Key = `${drawingConfig.responseIdentifier}_drawingBase64`;
        const jsonKey = `${drawingConfig.responseIdentifier}_jsonData`;
        const pendingSyncKey = `${drawingConfig.responseIdentifier}_pendingSync`;

        const imageBase64 = userResponses?.[base64Key]?.[0];
        const sceneJson = userResponses?.[jsonKey]?.[0];
        const pendingSync = userResponses?.[pendingSyncKey]?.[0] === 'true';

        if (imageBase64 && sceneJson) {
            try {
                const scene = JSON.parse(sceneJson);
                return {
                    imageUrl: imageBase64,
                    elements: scene.elements,
                    appState: scene.appState,
                    files: scene.files,
                    isOffline: pendingSync || this.isOffline(),
                };
            } catch (err) {
                console.error('Failed to parse scene JSON:', err);
                return null;
            }
        }

        if (this.isOffline()) {
            return null;
        }

        const imageId = userResponses?.[drawingConfig.responseIdentifier]?.[0];
        if (imageId && services?.uploadService && sceneJson) {
            try {
                const scene = JSON.parse(sceneJson);
                return {
                    imageUrl: services.uploadService.getFileDownloadUrl(imageId),
                    elements: scene.elements,
                    appState: scene.appState,
                    files: scene.files,
                    isOffline: false,
                };
            } catch (err) {
                console.error('Failed to parse scene JSON:', err);
                return null;
            }
        }

        return null;
    }


    static async preloadDrawingWithPresignedUrls(
        imagePresignedUrl,
        jsonPresignedUrl,
        question,
        drawingConfig,
        handleUserResponse,
        userResponses,
        forceFetch = false
    ) {
        if (!drawingConfig?.responseIdentifier) {
            return null;
        }

        if (this.isOffline()) {
            return null;
        }

        const existingBase64 = userResponses?.[`${drawingConfig?.responseIdentifier}_drawingBase64`]?.[0];
        const existingJsonData = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonData`]?.[0];

        if (!forceFetch) {
            const existingData = this.parseExistingDrawingData(existingBase64, existingJsonData);
            if (existingData) {
                return existingData;
            }
        }

        try {
            const [imageResponse, jsonResponse] = await Promise.all([
                fetch(imagePresignedUrl),
                fetch(jsonPresignedUrl),
            ]);

            const processed = await this.fetchAndProcessDrawingData(
                imageResponse,
                jsonResponse,
                existingBase64,
                existingJsonData
            );
            if (!processed) {
                return null;
            }

            this.storeDrawingDataInXML(
                question,
                drawingConfig,
                handleUserResponse,
                userResponses,
                processed.imageBase64,
                processed.sceneJson
            );

            return {
                imageUrl: processed.imageBase64,
                elements: processed.scene.elements,
                appState: processed.scene.appState,
                files: processed.scene.files,
                isOffline: false,
            };
        } catch (err) {
            console.error('Failed to preload drawing with presigned URLs:', err);
            return null;
        }
    }

    static async loadAndStoreS3Data(services, question, drawingConfig, handleUserResponse, userResponses) {
        if (!drawingConfig?.responseIdentifier) {
            return null;
        }

        const imageId = userResponses?.[drawingConfig?.responseIdentifier]?.[0];
        const jsonId = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonId`]?.[0];

        if (!imageId || !jsonId) {
            return null;
        }

        // Only fetch from S3 when online
        if (this.isOffline()) {
            return null;
        }

        const existingBase64 = userResponses?.[`${drawingConfig?.responseIdentifier}_drawingBase64`]?.[0];
        const existingJsonData = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonData`]?.[0];

        const existingData = this.parseExistingDrawingData(existingBase64, existingJsonData);
        if (existingData) {
            return existingData;
        }

        try {
            const imageUrl = services.uploadService.getFileDownloadUrl(imageId);
            const jsonUrl = services.uploadService.getFileDownloadUrl(jsonId);

            const [imageResponse, jsonResponse] = await Promise.all([
                fetch(imageUrl),
                fetch(jsonUrl),
            ]);

            const processed = await this.fetchAndProcessDrawingData(
                imageResponse,
                jsonResponse,
                null,
                null
            );
            if (!processed) {
                return null;
            }

            this.storeDrawingDataInXML(
                question,
                drawingConfig,
                handleUserResponse,
                userResponses,
                processed.imageBase64,
                processed.sceneJson,
                imageId
            );

            return {
                imageUrl: processed.imageBase64,
                elements: processed.scene.elements,
                appState: processed.scene.appState,
                files: processed.scene.files,
                isOffline: false,
            };
        } catch (err) {
            console.error('Failed to load and store S3 data:', err);
            return null;
        }
    }

    static async retryOfflineUploads(services, question, drawingConfig, questionResponse) {
        if (this.isOffline()) {
            return;
        }

        const pendingSyncResponse = questionResponse?.responseIdentifiers?.find(
            r => r.identifier === `${drawingConfig.responseIdentifier}_pendingSync`
        );
        const imageBase64Response = questionResponse?.responseIdentifiers?.find(
            r => r.identifier === `${drawingConfig.responseIdentifier}_drawingBase64`
        );
        const sceneJsonResponse = questionResponse?.responseIdentifiers?.find(
            r => r.identifier === `${drawingConfig.responseIdentifier}_jsonData`
        );

        const pendingSync = this.isPendingSyncTrue(pendingSyncResponse);

        if (!pendingSync) {
            return;
        }

        const imageBase64 = imageBase64Response?.userResponse?.[0];
        const sceneJson = sceneJsonResponse?.userResponse?.[0];

        if (!imageBase64 || !sceneJson) {
            return;
        }

        // Use question.contentRevisionId - this is what we stored when saving offline
        const contentRevisionId = question.contentRevisionId;

        const { blob: imageBlob } = this.createImageBlob(imageBase64, `${contentRevisionId}-${drawingConfig.responseIdentifier}.png`);
        let scene;
        try {
            scene = JSON.parse(sceneJson);
        } catch (err) {
            console.error('Failed to parse scene JSON for retry:', err);
            return;
        }
        const jsonBlob = this.createJsonBlob(scene, `${contentRevisionId}-${drawingConfig.responseIdentifier}.json`);

        await this.uploadBlobsToS3(
            imageBlob,
            jsonBlob,
            services,
            question,
            drawingConfig,
            sceneJson,
            contentRevisionId
        );
    }

    static async uploadBlobsToS3(imageBlob, jsonBlob, services, question, drawingConfig, sceneJson = null, contentRevisionId = null) {
        const skipContentUrlCreation = settingsReader.shouldStoreDrawingsAsUri();

        return new Promise((resolve, reject) => {
            const uploadState = {
                imageContentItemId: null,
                jsonContentItemId: null,
                imageS3Uri: null,
                jsonS3Uri: null,
                imageUploaded: false,
                jsonUploaded: false,
            };

            const onBothUploaded = () => {
                const revisionId = contentRevisionId || question.contentRevisionId;
                this.handleBothUploadsComplete({
                    uploadState,
                    skipContentUrlCreation,
                    services,
                    question,
                    sceneData: { sceneJson },
                    drawingConfig,
                    fileInfo: { fileName: imageBlob.name, fileType: imageBlob.type, imageBlob },
                    contentRevisionId: revisionId,
                    handleUserResponse: this.getResponseService().handleUserResponse.bind(this.getResponseService()),
                    resolve,
                });
            };

            const uploadCallback = this.createUploadCallback({
                skipContentUrlCreation,
                fileNames: {
                    imageFileName: imageBlob.name,
                    jsonFileName: jsonBlob.name,
                    imageBlobName: imageBlob.name,
                    jsonBlobName: jsonBlob.name,
                },
                state: uploadState,
                onBothUploaded,
                onError: reject,
            });

            services.uploadService.upload(imageBlob, question.contentItemId, question.contentRevisionId, uploadCallback, skipContentUrlCreation);
            services.uploadService.upload(jsonBlob, question.contentItemId, question.contentRevisionId, uploadCallback, skipContentUrlCreation);
        });
    }

    static cleanupOfflineData(contentRevisionId, responseIdentifier) {
        const cleanupResponses = [
            {
                identifier: `${responseIdentifier}_drawingBase64`,
                candidateResponse: [],
            },
            {
                identifier: `${responseIdentifier}_jsonData`,
                candidateResponse: [],
            },
            {
                identifier: `${responseIdentifier}_pendingSync`,
                candidateResponse: [],
            },
        ];

        for (const cleanupResponse of cleanupResponses) {
            this.getResponseService().handleUserResponse(contentRevisionId, cleanupResponse.identifier, [], []);
        }
    }

    static createDeleteDrawingExtraResponses(responseIdentifier) {
        // Clean up all drawing-related extra response identifiers, including offline fields
        const drawingExtraIdentifiers = this.getDrawingExtraIdentifiers(responseIdentifier);
        return drawingExtraIdentifiers.map(identifier => ({
            identifier,
            candidateResponse: [],
        }));
    }

    static findDrawingResponseIdentifier(questionResponse, question) {
        let drawingResponseIdentifier = null;
        let jsonIdResponse = null;

        for (const response of questionResponse.responseIdentifiers) {
            if (response.identifier.endsWith('_fileURI') && response.userResponse?.[0]) {
                drawingResponseIdentifier = response.identifier.replace('_fileURI', '');
                break;
            }
            if (response.identifier.endsWith('_jsonDataURI') && response.userResponse?.[0]) {
                drawingResponseIdentifier = response.identifier.replace('_jsonDataURI', '');
                break;
            }
            if (response.identifier.endsWith('_jsonId') && response.userResponse?.[0]) {
                drawingResponseIdentifier = response.identifier.replace('_jsonId', '');
                jsonIdResponse = response;
                break;
            }
        }

        if (!drawingResponseIdentifier) {
            const drawingConfig = DrawingCanvasService.getDrawingConfig(question);
            if (drawingConfig?.enabled && drawingConfig?.responseIdentifier) {
                drawingResponseIdentifier = drawingConfig.responseIdentifier;
                jsonIdResponse = questionResponse.responseIdentifiers.find(
                    r => r.identifier === `${drawingConfig.responseIdentifier}_jsonId`
                );
            }
        }

        return { drawingResponseIdentifier, jsonIdResponse };
    }

    static preloadDrawingForQuestion(contentRevisionId, responses, services) {
        const question = assessmentReader.getQuestion(contentRevisionId);
        if (!question) {
            return null;
        }

        const questionResponse = responses[contentRevisionId];
        if (!questionResponse?.responseIdentifiers) {
            return null;
        }

        const { drawingResponseIdentifier, jsonIdResponse } = this.findDrawingResponseIdentifier(questionResponse, question);
        if (!drawingResponseIdentifier) {
            return null;
        }

        const mainResponse = questionResponse.responseIdentifiers.find(
            r => r.identifier === drawingResponseIdentifier
        );

        const imageId = mainResponse?.userResponse?.[0];
        const jsonId = jsonIdResponse?.userResponse?.[0];

        const drawingConfigForPreload = {
            enabled: true,
            responseIdentifier: drawingResponseIdentifier,
        };

        const userResponsesObj = {};
        for (const r of questionResponse.responseIdentifiers) {
            userResponsesObj[r.identifier] = r.userResponse;
        }

        if (settingsReader.shouldStoreDrawingsAsUri()) {
            const presignedPromise = this.tryPreloadWithPresignedUrls(
                questionResponse,
                drawingResponseIdentifier,
                question,
                drawingConfigForPreload,
                userResponsesObj
            );
            if (presignedPromise !== null) {
                return presignedPromise;
            }
        }

        if (!imageId || !jsonId) {
            return null;
        }

        if (this.hasExistingOfflineData(questionResponse, drawingResponseIdentifier)) {
            return null;
        }

        return this.loadAndStoreS3Data(
            services,
            question,
            drawingConfigForPreload,
            this.getResponseService().handleUserResponse.bind(this.getResponseService()),
            userResponsesObj
        );
    }

    static tryPreloadWithPresignedUrls(questionResponse, drawingResponseIdentifier, question, drawingConfig, userResponsesObj) {
        const { imagePresignedUrl, jsonPresignedUrl } = this.resolvePresignedUrls(
            questionResponse,
            drawingResponseIdentifier
        );

        if (imagePresignedUrl && jsonPresignedUrl) {
            return this.preloadDrawingWithPresignedUrls(
                imagePresignedUrl,
                jsonPresignedUrl,
                question,
                drawingConfig,
                this.getResponseService().handleUserResponse.bind(this.getResponseService()),
                userResponsesObj,
                false
            );
        }
        return null;
    }

    static hasExistingOfflineData(questionResponse, drawingResponseIdentifier) {
        const existingBase64 = questionResponse.responseIdentifiers.find(
            r => r.identifier === `${drawingResponseIdentifier}_drawingBase64`
        );
        const existingJsonData = questionResponse.responseIdentifiers.find(
            r => r.identifier === `${drawingResponseIdentifier}_jsonData`
        );
        return !!(existingBase64?.userResponse?.[0] && existingJsonData?.userResponse?.[0]);
    }

    static async preloadAllCanvasBackgrounds(assessment = null) {
        if (this.isOffline() || !settingsReader.isCandidateDrawingOptionEnabled()) {
            return;
        }

        const services = questionHelperServices.getServices();
        const assessmentToUse = assessment || assessmentReader.getAssessment();

        if (!services?.uploadService || !assessmentToUse?.sections) {
            return;
        }

        if (!excalidrawPreloadService.hasDrawingInteractions(assessmentToUse)) {
            return;
        }

        const handleUserResponse = this.getResponseService().handleUserResponse.bind(this.getResponseService());
        const state = storeManager.getState();
        const allQuestions = assessmentToUse.sections.flatMap(section => section.questions || []);

        const preloadPromises = allQuestions
            .map((questionFromSection) => {
                const contentRevisionId = questionFromSection.contentRevisionId;
                const question = assessmentReader.getQuestion(contentRevisionId);

                if (!question || !question.isParsed || !question.responseModels) {
                    return null;
                }

                const drawingConfig = this.getDrawingConfig(question);
                const canvasBackground = drawingConfig?.canvasBackground;

                if (canvasBackground?.contentItemId && canvasBackground?.jsonContentItemId) {
                    const questionResponse = state.response[contentRevisionId];
                    const userResponses = {};
                    if (questionResponse?.responseIdentifiers) {
                        for (const r of questionResponse.responseIdentifiers) {
                            userResponses[r.identifier] = r.userResponse;
                        }
                    }

                    return this.preloadCanvasBackground(
                        question,
                        drawingConfig,
                        handleUserResponse,
                        userResponses,
                        canvasBackground.contentItemId,
                        canvasBackground.jsonContentItemId,
                        services
                    );
                }

                return null;
            })
            .filter(promise => promise !== null);

        await Promise.all(preloadPromises);
    }

    static async preloadAllDrawingsForOffline(assessment = null) {
        if (this.isOffline() || !settingsReader.isCandidateDrawingOptionEnabled()) {
            return;
        }

        const services = questionHelperServices.getServices();
        const assessmentToUse = assessment || assessmentReader.getAssessment();

        if (!services?.uploadService || !assessmentToUse?.sections) {
            return;
        }

        if (!excalidrawPreloadService.hasDrawingInteractions(assessmentToUse)) {
            return;
        }

        const state = storeManager.getState();
        const responses = state.response;

        const allQuestionIds = assessmentToUse.sections.flatMap(section =>
            (section.questions || []).map(question => question.contentRevisionId)
        );

        const preloadPromises = allQuestionIds
            .map(contentRevisionId => this.preloadDrawingForQuestion(contentRevisionId, responses, services))
            .filter(promise => promise !== null);

        await Promise.all(preloadPromises);
        await this.preloadAllCanvasBackgrounds(assessmentToUse);
    }

    static clearPendingSyncForAllDrawings() {
        if (!settingsReader.isCandidateDrawingOptionEnabled()) {
            return;
        }

        const state = storeManager.getState();
        const responses = state.response;

        for (const contentRevisionIdKey of Object.keys(responses)) {
            const contentRevisionId = Number.parseInt(contentRevisionIdKey, 10);
            const questionResponse = responses[contentRevisionIdKey];
            const identifiers = questionResponse?.responseIdentifiers;

            if (Array.isArray(identifiers)) {
                identifiers.forEach((response) => {
                    if (this.isPendingSyncTrue(response) && this.isDrawingResponseIdentifier(response.identifier)) {
                        this.getResponseService().handleUserResponse(
                            contentRevisionId,
                            response.identifier,
                            ['false'],
                            []
                        );
                    }
                });
            }
        }
    }

    static async retryAllOfflineDrawings() {
        if (this.isOffline() || !settingsReader.isCandidateDrawingOptionEnabled()) {
            return;
        }

        const services = questionHelperServices.getServices();

        const state = storeManager.getState();
        const responses = state.response;

        const offlineDrawings = this.collectPendingDrawingResponses(responses);

        const uploadPromises = offlineDrawings
            .map((drawing) => {
                const question = assessmentReader.getQuestion(drawing.contentRevisionId);
                if (!question) {
                    return null;
                }

                const baseConfig = DrawingCanvasService.getDrawingConfig(question);
                if (!baseConfig.enabled) {
                    return null;
                }

                const drawingConfig = { ...baseConfig, responseIdentifier: drawing.responseIdentifier };

                const questionResponse = responses[drawing.contentRevisionId];

                return DrawingCanvasService.retryOfflineUploads(
                    services,
                    question,
                    drawingConfig,
                    questionResponse
                );
            })
            .filter(promise => promise !== null);

        await Promise.all(uploadPromises);
        this.clearPendingSyncForAllDrawings();
    }

    static collectPendingDrawingResponses(responses) {
        const pending = [];
        Object.keys(responses).forEach((contentRevisionIdKey) => {
            const questionResponse = responses[contentRevisionIdKey];
            const identifiers = questionResponse?.responseIdentifiers;

            if (Array.isArray(identifiers)) {
                identifiers.forEach((response) => {
                    if (!this.isPendingSyncTrue(response)) {
                        return;
                    }
                    const baseResponseId = response.identifier.replace('_pendingSync', '');
                    pending.push({
                        contentRevisionId: Number.parseInt(contentRevisionIdKey, 10),
                        responseIdentifier: baseResponseId,
                    });
                });
            }
        });
        return pending;
    }

    static isPendingSyncTrue(response) {
        if (!response?.identifier?.endsWith('_pendingSync')) {
            return false;
        }
        const pendingValue = response.userResponse?.[0];
        if (pendingValue === undefined || pendingValue === null) {
            return true;
        }
        return pendingValue === 'true' || pendingValue === true;
    }

    static getExcalidrawLanguageCode(currentLanguage) {
        const excalidrawLanguages = excalidrawPreloadService.getLanguages();
        const defaultLang = excalidrawPreloadService.getDefaultLang();

        if (!excalidrawLanguages || !defaultLang) {
            return 'en';
        }

        const supportedCodes = excalidrawLanguages.map(lang => lang.code);
        const lowerCurrentLanguage = (currentLanguage || 'en-US').toLowerCase();

        const exactMatch = supportedCodes.find(code => code.toLowerCase() === lowerCurrentLanguage);
        if (exactMatch) {
            return exactMatch;
        }

        const baseCode = lowerCurrentLanguage.split('-')[0];
        const baseMatch = supportedCodes.find((code) => {
            const lower = code.toLowerCase();
            return lower === baseCode || lower.startsWith(`${baseCode}-`);
        });
        if (baseMatch) {
            return baseMatch;
        }

        return defaultLang.code;
    }
}

export default DrawingCanvasService;
